  import React, { useState, useRef, useEffect, useCallback } from 'react';
  import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Snackbar,
    Alert,
    AccordionDetails,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Checkbox,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Toolbar,
    CircularProgress,
    Tooltip,
    Stack,
  } from '@mui/material';
  import { alpha } from '@mui/material/styles';
  import SearchIcon from '@mui/icons-material/Search';
  import InputBase from '@mui/material/InputBase';
  import { Add as AddIcon, Download as DownloadIcon, Save as SaveIcon , Delete as DeleteIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
  import { useCompetencia } from '../../../../context/CompetenciaContext';
  import PruebasTable from './PruebasTable';
  import PredefinedPruebasModal from './PredefinedPruebasModal';
  import CustomAccordion from '../../../../components/CustomAccordion';
  import SigertreLoader from '../../../../components/Common/SigertreLoader';
  import CustomPruebaModal from './CustomPruebaModal';
  import MobilePruebasView from './Movil/MobilePruebasView';
  import { useTheme, useMediaQuery } from '@mui/material';

  const PruebasManager = ({ competencia }) => {
    const {
      addPrueba,
      updatePrueba,
      deletePrueba,
      fetchPruebas,
      updateAdvancedRules,
      updateRelayOptions,
      exportPruebasToCSV,
      importPruebasFromCSV,
      getCategorias, // Asegurarse que está importado del contexto
    } = useCompetencia();

    // --- Estados ---
    const [newPrueba, setNewPrueba] = useState({
      nombre: '',
      categoriaIdsFemenil: [],
      categoriaIdsVaronil: [],
      testType: '',
      tipoMarca: '',
      eventCode: '',
    });

    const [newPruebaCustom, setNewPruebaCustom] = useState({
      id: '', // Opcional para edición
      nombre: '',
      testType: '',
      eventCode: '',
      tipoMarca: '',
      categoriaIdsFemenil: [],
      categoriaIdsVaronil: []
    });

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [openErrorModal, setOpenErrorModal] = useState(false);
    const [errorModalContent, setErrorModalContent] = useState([]);
    const [openPredefinedModal, setOpenPredefinedModal] = useState(false);
    const [openCustomPruebaModal, setOpenCustomPruebaModal] = useState(false);
    const [openRelevosModal, setOpenRelevosModal] = useState(false);
    const [categorias, setCategorias] = useState([]);
    const [opcionesRelevosForm, setOpcionesRelevosForm] = useState({
      minMiembros: 0,
      maxMiembros: 4,
      permitirEquiposMixtos: false,
      permitirSuplentes: false,
      // maxRelevosPorRegistro: 0, // Esto parece estar en opciones avanzadas, no aquí
      numeroSuplentes: 0,
    });
    // const { getCategorias } = useCompetencia(); // Ya desestructurado arriba

      // *** NUEVO ESTADO PARA OPCIONES AVANZADAS ***
      const [advancedRulesForm, setAdvancedRulesForm] = useState({
        maxPruebasIndPorRegistro: 3,
        maxRelevosPorRegistro: 1,
        habilitarMarcasSiembra: false,
        requerirMiembrosRelevo: false,
      });
      const [hasAdvancedChanges, setHasAdvancedChanges] = useState(false); // Para habilitar el botón Guardar

    const [selectedErrorRows, setSelectedErrorRows] = useState(new Set());
    const fileInputRef = useRef(null);
    const [pruebas, setPruebas] = useState([]);
    const [isLoading, setIsLoading] = useState({
      pruebas: true,
      acciones: false
    });
    const [search, setSearch] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedPruebaIds, setSelectedPruebaIds] = useState([]);

    // --- Variables Derivadas ---
    const competenciaId = competencia?.id;

    // --- Funciones Memoizadas (Callbacks) ---

    const filteredPruebas = React.useMemo(() => {
    const q = (search || '').toLowerCase();
    if (!q) return pruebas;
    return pruebas.filter(p =>
      p?.nombre?.toLowerCase().includes(q) ||
      p?.eventCode?.toLowerCase?.().includes(q)
    );
  }, [pruebas, search]);

    // Función para cargar pruebas (ya estaba con useCallback)
    const loadPruebas = useCallback(async () => {
      if (!competenciaId) return;
      setIsLoading(prev => ({ ...prev, pruebas: true, acciones: prev.acciones })); // No resetear acciones
      try {
        const pruebasData = await fetchPruebas(competenciaId);
        setPruebas(pruebasData);
      } catch (error) {
        console.error("Error cargando pruebas:", error);
        setSnackbar({ open: true, message: 'Error al cargar pruebas.', severity: 'error' });
      } finally {
        setIsLoading(prev => ({ ...prev, pruebas: false }));
      }
    }, [competenciaId, fetchPruebas, setIsLoading, setSnackbar]); // Añadido setIsLoading y setSnackbar a dependencias

    // Función para cargar categorías (memoizada)
    const loadCategorias = useCallback(async () => {
      if (competenciaId) {
        // No es necesario setIsLoading aquí a menos que quieras un loader específico para categorías
        try {
          const categoriasData = await getCategorias(competenciaId);
          setCategorias(categoriasData);
        } catch (error) {
          console.error("Error cargando categorías:", error);
          setSnackbar({ open: true, message: 'Error al cargar categorías.', severity: 'error' });
        }
      } else {
        setCategorias([]); // Limpiar categorías si no hay competenciaId
      }
    }, [competenciaId, getCategorias, setSnackbar]); // Añadido setSnackbar

    const refreshPruebas = useCallback(async () => {
    await loadPruebas();
    }, [loadPruebas]);

    // **Handler para Actualizar Prueba (Memoizado)**
    // Esta función se pasará a PruebasTable y luego a PruebaRow
    const handleUpdatePrueba = useCallback(async (pruebaId, updatedData, opts = {}) => {
      const { silent = false, skipReload = false } = opts;
      if (!silent) setIsLoading(prev => ({ ...prev, acciones: true }));
      try {
        await updatePrueba(competenciaId, pruebaId, {
          ...updatedData,
          // Asegurarse de enviar arrays vacíos si no hay categorías seleccionadas
          categoriaIdsFemenil: updatedData.categoriaIdsFemenil || [],
          categoriaIdsVaronil: updatedData.categoriaIdsVaronil || [],
          // Asegurar que otros campos necesarios se envíen si es requerido por el backend
          tipoMarca: updatedData.tipoMarca || '', // O un valor por defecto adecuado
          eventCode: updatedData.eventCode || '', // O un valor por defecto adecuado
          descripcion: updatedData.descripcion || '',
        });
        if (!skipReload) await loadPruebas();
        if (!silent) {
          setSnackbar({ open: true, message: 'Prueba actualizada exitosamente.', severity: 'success' });
        }
        return { ok: true };
      } catch (error) {
        console.error("Error actualizando prueba:", error);
        if (!silent) {
          setSnackbar({ open: true, message: `Error al actualizar: ${error.message || 'Error desconocido'}`, severity: 'error' });
        }
        return { ok: false, error };
      } finally {
        if (!silent) setIsLoading(prev => ({ ...prev, acciones: false }));
      }
    }, [competenciaId, updatePrueba, loadPruebas, setSnackbar, setIsLoading]); // Dependencias

    // **Handler para Eliminar Prueba (Memoizado)**
    // Se usa tanto para borrado individual (desde PruebaRow) como masivo (desde PruebasTable)
    const handleDeletePrueba = useCallback(async (pruebaId) => {
      // No activar isLoading aquí si el borrado masivo tiene su propio loader en PruebasTable
      // setIsLoading(prev => ({ ...prev, acciones: true }));
      try {
        await deletePrueba(competenciaId, pruebaId);
        await loadPruebas(); // Recargar la lista
        // El snackbar para borrado masivo se maneja en PruebasTable.
        // Este snackbar es más útil para el borrado individual desde el modal de PruebaRow.
        // Considera si quieres diferenciar los mensajes.
        // setSnackbar({ open: true, message: 'Prueba eliminada.', severity: 'info' });
        return { wasDeleted: true }; // Para Promise.all en PruebasTable
      } catch (error) {
        console.error("Error eliminando prueba:", error);
        setSnackbar({ open: true, message: `Error al eliminar: ${error.message || 'Error desconocido'}`, severity: 'error' });
        return { wasDeleted: false, reason: error.message || 'Error desconocido' }; // Para Promise.all
      } finally {
        // No desactivar isLoading aquí si no se activó
        // setIsLoading(prev => ({ ...prev, acciones: false }));
      }
    }, [competenciaId, deletePrueba, loadPruebas, setSnackbar]); // Dependencias

    // --- Efectos ---

    // Cargar pruebas cuando el componente se monta o loadPruebas cambia (debido a competenciaId)
    useEffect(() => {
      loadPruebas();
    }, [loadPruebas]);

    // Cargar categorías cuando el componente se monta o loadCategorias cambia (debido a competenciaId)
    useEffect(() => {
      loadCategorias();
    }, [loadCategorias]);

    // Cargar opciones de relevos cuando se abre el modal correspondiente
    // *** MODIFICADO: Sincronizar OPCIONES DE RELEVOS con la competencia ***
    useEffect(() => {
      // Este efecto se ejecuta cuando 'competencia' (y específicamente sus opciones de relevos) cambia.
      // Actualiza el estado LOCAL del formulario de relevos con los datos de Firestore.
      const relevosData = competencia?.rules?.opcionesRelevos; // Acceder a la ruta correcta
      if (relevosData) {
        console.log("PRUEBAS MANAGER: Sincronizando opcionesRelevosForm con datos de competencia:", relevosData);
        setOpcionesRelevosForm({
          minMiembros: relevosData.minMiembros ?? 0,
          maxMiembros: relevosData.maxMiembros ?? 4,
          permitirEquiposMixtos: relevosData.permitirEquiposMixtos ?? false,
          permitirSuplentes: relevosData.permitirSuplentes ?? false,
          numeroSuplentes: relevosData.numeroSuplentes ?? 0,
        });
      } else {
        // Si no hay datos en la competencia (ej. competencia nueva), usar/resetear a defaults
        console.log("PRUEBAS MANAGER: No se encontraron opciones de relevos en la competencia, usando defaults.");
        setOpcionesRelevosForm({
            minMiembros: 0,
            maxMiembros: 4,
            permitirEquiposMixtos: false,
            permitirSuplentes: false,
            numeroSuplentes: 0,
        });
      }
      // Depender directamente de la parte del objeto 'competencia' que contiene estos datos
    }, [competencia?.rules?.opcionesRelevos]);

    // Actualizar opciones de relevos en Firestore cuando cambian en el formulario

    useEffect(() => {
      if (competencia?.rules) {
        // Inicializa o resetea el formulario local con los datos de la competencia actual
        setAdvancedRulesForm({
          maxPruebasIndPorRegistro: competencia.rules.maxPruebasIndPorRegistro ?? 3,
          maxRelevosPorRegistro: competencia.rules.maxRelevosPorRegistro ?? 1,
          habilitarMarcasSiembra: competencia.rules.habilitarMarcasSiembra ?? false,
          requerirMiembrosRelevo: competencia.rules.requerirMiembrosRelevo ?? false,
        });
        setHasAdvancedChanges(false); // Resetea el flag de cambios al cargar/recargar
      }
    }, [competencia?.rules]); // Se ejecuta si las reglas de la competencia cambian

    // --- Handlers Internos (No necesitan useCallback para optimización de hijos) ---

    const handleAddPrueba = async () => {
      setIsLoading(prev => ({ ...prev, acciones: true }));
      try {
        const { nombre, categoriaIdsFemenil, categoriaIdsVaronil, testType, eventCode, tipoMarca } = newPrueba; // Incluir eventCode y tipoMarca si se usan en este form

        // Validación más robusta
        if (!nombre?.trim() || !testType || (categoriaIdsFemenil.length === 0 && categoriaIdsVaronil.length === 0)) {
          let missingFields = [];
          if (!nombre?.trim()) missingFields.push("Nombre");
          if (!testType) missingFields.push("Tipo de Prueba");
          if (categoriaIdsFemenil.length === 0 && categoriaIdsVaronil.length === 0) missingFields.push("Categorías");
          throw new Error(`Campos obligatorios faltantes: ${missingFields.join(', ')}`);
        }

        await addPrueba(competenciaId, {
          nombre: nombre.trim(),
          descripcion: '', // O añadir campo de descripción si existe
          categoriaIdsFemenil,
          categoriaIdsVaronil,
          testType,
          eventCode: eventCode || '', // Asegurar valor
          tipoMarca: tipoMarca || '', // Asegurar valor
        });

        await loadPruebas();
        // Resetear el formulario de nueva prueba
        setNewPrueba({ nombre: '', categoriaIdsFemenil: [], categoriaIdsVaronil: [], testType: '', tipoMarca: '', eventCode: '' });
        setSnackbar({ open: true, message: 'Prueba añadida exitosamente.', severity: 'success' });
      } catch (error) {
        setSnackbar({ open: true, message: error.message, severity: 'error' });
      } finally {
        setIsLoading(prev => ({ ...prev, acciones: false }));
      }
    };


    const handleAddPruebaCustom = async () => {
      setIsLoading(prev => ({ ...prev, acciones: true }));
      try {
        const { nombre, categoriaIdsFemenil, categoriaIdsVaronil, testType, eventCode, tipoMarca } = newPruebaCustom;

        // Validación desde el modal personalizado
        if (!nombre?.trim() || !testType || !eventCode || !tipoMarca || (categoriaIdsFemenil.length === 0 && categoriaIdsVaronil.length === 0)) {
          let missingFields = [];
          if (!nombre?.trim()) missingFields.push("Nombre");
          if (!testType) missingFields.push("Tipo");
          if (!eventCode) missingFields.push("Código/Valor");
          if (!tipoMarca) missingFields.push("Tipo Marca (auto)"); // Revisar si este siempre se calcula
          if (categoriaIdsFemenil.length === 0 && categoriaIdsVaronil.length === 0) missingFields.push("Categorías");
          throw new Error(`Campos obligatorios faltantes: ${missingFields.join(', ')}`);
        }

        await addPrueba(competenciaId, {
            ...newPruebaCustom,
            nombre: newPruebaCustom.nombre.trim(), // Asegurar trim
        });

        await loadPruebas();
        // Resetear el formulario del modal personalizado
        setNewPruebaCustom({ id: '', nombre: '', testType: '', eventCode: '', tipoMarca: '', categoriaIdsFemenil: [], categoriaIdsVaronil: [] });
        setOpenCustomPruebaModal(false); // Cerrar modal
        setSnackbar({ open: true, message: 'Prueba personalizada añadida.', severity: 'success' });
      } catch (error) {
        setSnackbar({ open: true, message: error.message, severity: 'error' });
        // No cerrar el modal en caso de error para que el usuario corrija
      } finally {
        setIsLoading(prev => ({ ...prev, acciones: false }));
      }
    };

    const handleAddSelectedPruebas = async (pruebasToAdd) => {
      if (pruebasToAdd.length === 0) {
        setSnackbar({ open: true, message: 'No seleccionaste ninguna prueba predeterminada.', severity: 'warning' });
        return;
      }

      setIsLoading(prev => ({ ...prev, acciones: true }));
      let addedCount = 0;
      let errorCount = 0;

      try {
        // Usar Promise.allSettled para manejar errores individuales
        const results = await Promise.allSettled(
          pruebasToAdd.map(prueba =>
            addPrueba(competenciaId, {
              nombre: prueba.nombre,
              descripcion: prueba.descripcion || '',
              // Permitir añadir sin categorías inicialmente o asignar todas? Decidir política.
              // Aquí se añaden sin categorías:
              categoriaIdsFemenil: [],
              categoriaIdsVaronil: [],
              testType: prueba.testType || '', // Asegurar valor
              eventCode: prueba.eventCode || '', // Asegurar valor
              tipoMarca: prueba.tipoMarca || '', // Asegurar valor
            })
          )
        );

        results.forEach(result => {
          if (result.status === 'fulfilled') {
            addedCount++;
          } else {
            errorCount++;
            console.error("Error añadiendo prueba predefinida:", result.reason);
          }
        });

        if (addedCount > 0) {
          await loadPruebas(); // Recargar si se añadió al menos una
          setSnackbar({ open: true, message: `${addedCount} pruebas predeterminadas añadidas.`, severity: 'success' });
        }
        if (errorCount > 0) {
          setSnackbar({ open: true, message: `No se pudieron añadir ${errorCount} pruebas predeterminadas. Revise la consola.`, severity: 'error' });
        }
        setOpenPredefinedModal(false); // Cerrar modal

      } catch (error) { // Error inesperado en el proceso general
        setSnackbar({ open: true, message: `Error inesperado: ${error.message}`, severity: 'error' });
      } finally {
        setIsLoading(prev => ({ ...prev, acciones: false }));
      }
    };


    const handleOpcionesAvanzadasChange = useCallback((field, value) => {
      // Usar useCallback si esta función se pasa a otro componente memoizado
      // Por ahora, asumiendo que solo actualiza el contexto/estado localmente
      updateAdvancedRules(competenciaId, { [field]: value })
        .then(() => {
          // Opcional: Snackbar de éxito para opciones avanzadas
          // setSnackbar({ open: true, message: 'Opción avanzada actualizada.', severity: 'info' });
        })
        .catch(error => {
          console.error("Error actualizando opciones avanzadas:", error);
          setSnackbar({ open: true, message: `Error al guardar opción: ${error.message}`, severity: 'error' });
        });
    }, [competenciaId, updateAdvancedRules, setSnackbar]); // Añadidas dependencias


    const handleOpcionesRelevosChange = (field, value) => {
      setOpcionesRelevosForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    };


    const handleSaveOpcionesRelevos = () => {
      const { minMiembros, maxMiembros, permitirEquiposMixtos, permitirSuplentes, numeroSuplentes } = opcionesRelevosForm;

      // Validaciones
      if (minMiembros < 0 || maxMiembros <= 0 || numeroSuplentes < 0) {
          setSnackbar({ open: true, message: 'Los valores numéricos no pueden ser negativos.', severity: 'warning' });
          return;
      }
      if (minMiembros > maxMiembros) {
        setSnackbar({ open: true, message: 'El mínimo de miembros no puede ser mayor al máximo.', severity: 'warning' });
        return;
      }
      if (permitirSuplentes && numeroSuplentes === 0) {
          setSnackbar({ open: true, message: 'Si se permiten suplentes, indique cuántos (mínimo 1).', severity: 'warning' });
          return;
      }
      if (!permitirSuplentes && numeroSuplentes > 0) {

          setIsLoading(prev => ({ ...prev, acciones: true })); // Añadir feedback de carga
          // Opcional: resetear numeroSuplentes si no se permiten
          setOpcionesRelevosForm(prev => ({ ...prev, numeroSuplentes: 0 }));
          // Llama a update con numeroSuplentes = 0
          updateRelayOptions(competenciaId, opcionesRelevosForm)
          .then((result) => { // Asume que updateRelayOptions devuelve {success: true/false}
              if (result.success) {
                setSnackbar({ open: true, message: 'Opciones de relevos guardadas.', severity: 'success' });
                setOpenRelevosModal(false);
              } else {
                throw result.error || new Error("Error desconocido al guardar opciones de relevos.");
              }
          })
          .catch(error => {
            console.error("Error guardando opciones de relevos:", error);
            setSnackbar({ open: true, message: `Error al guardar relevos: ${error.message}`, severity: 'error' });
              // No cerrar el modal en caso de error
          })
          .finally(() => {
              setIsLoading(prev => ({ ...prev, acciones: false })); // Finalizar feedback de carga
          });
          return; // Salir después de corregir y guardar
      }


      // Llamada a la función del contexto para guardar
      updateRelayOptions(competenciaId, opcionesRelevosForm)
        .then(() => {
          setSnackbar({ open: true, message: 'Opciones de relevos guardadas.', severity: 'success' });
          setOpenRelevosModal(false);
        })
        .catch(error => {
          console.error("Error guardando opciones de relevos:", error);
          setSnackbar({ open: true, message: `Error al guardar: ${error.message}`, severity: 'error' });
        });
    };


    const handleDownloadCSV = () => {
      setIsLoading(prev => ({ ...prev, acciones: true }));
      try {
        exportPruebasToCSV(competenciaId);
        setSnackbar({ open: true, message: 'Descarga de CSV iniciada.', severity: 'success' });
      } catch (error) {
        console.error("Error exportando CSV:", error);
        setSnackbar({ open: true, message: `Error al exportar: ${error.message}`, severity: 'error' });
      } finally {
        setIsLoading(prev => ({ ...prev, acciones: false }));
      }
    };


    const handleSelectFile = () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Resetear para permitir seleccionar el mismo archivo
        fileInputRef.current.click();
      }
    };


    const handleImportCSV = (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsLoading(prev => ({ ...prev, acciones: true }));
      importPruebasFromCSV(competenciaId, file)
        .then(async (result) => { // Marcar como async si se usa await dentro
          const { wasImported = false, count = 0, errores = [] } = result || {}; // Valores por defecto

          let messages = [];
          let finalSeverity = 'info';

          if (wasImported && count > 0) {
            messages.push(`${count} pruebas importadas.`);
            finalSeverity = 'success';
            await loadPruebas(); // Recargar pruebas si hubo importación exitosa
          } else if (!wasImported && errores.length === 0) {
              messages.push('No se importaron nuevas pruebas del archivo.');
          }

          if (errores.length > 0) {
            setErrorModalContent(errores);
            setOpenErrorModal(true);
            messages.push(`${errores.length} fila${errores.length > 1 ? 's' : ''} con errores.`);
            finalSeverity = finalSeverity === 'success' ? 'warning' : 'error'; // Warning si hubo éxito parcial, error si solo hubo fallos
          }

          if (messages.length > 0) {
              setSnackbar({ open: true, message: messages.join(' '), severity: finalSeverity });
          }

        })
        .catch((error) => {
          console.error("Error importando CSV:", error);
          let errorMessage = 'Error desconocido al importar pruebas.';
          if (Array.isArray(error)) { // Errores específicos de parsing devueltos como array
            errorMessage = `Errores al procesar CSV: ${error.map(e => e.message || JSON.stringify(e)).join('. ')}`;
          } else if (error.message) {
            errorMessage = `Error al importar: ${error.message}`;
          }
          setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        })
        .finally(() => {
          setIsLoading(prev => ({ ...prev, acciones: false }));
        });
    };
      // *** MODIFICADO: Handler local para cambios en Opciones Avanzadas ***
      const handleAdvancedRulesFormChange = useCallback((field, value) => {
        setAdvancedRulesForm(prev => ({
          ...prev,
          [field]: value,
        }));
        setHasAdvancedChanges(true); // Marcar que hay cambios pendientes
      }, []); // No necesita dependencias complejas
    
      // *** NUEVO: Handler para guardar las Opciones Avanzadas ***
      const handleSaveAdvancedRules = async () => {
        if (!competenciaId || !hasAdvancedChanges) return;
    
        setIsLoading(prev => ({ ...prev, acciones: true }));
        try {
          // Llama a la función del contexto con el estado local COMPLETO
          const result = await updateAdvancedRules(competenciaId, advancedRulesForm);
    
          if (result.success) {
            setSnackbar({ open: true, message: 'Opciones avanzadas guardadas exitosamente.', severity: 'success' });
            setHasAdvancedChanges(false); // Resetea el flag después de guardar
            // No necesitas recargar aquí si updateAdvancedRules actualiza el estado del contexto
            // y tu useEffect [competencia?.rules] ya reacciona a eso.
          } else {
            throw result.error || new Error("Error desconocido al guardar opciones avanzadas.");
          }
        } catch (error) {
          console.error("Error guardando opciones avanzadas:", error);
          setSnackbar({ open: true, message: `Error al guardar opciones: ${error.message}`, severity: 'error' });
        } finally {
          setIsLoading(prev => ({ ...prev, acciones: false }));
        }
      };


    const handleDeleteSelectedErrors = () => {
      const remainingErrors = errorModalContent.filter((_, index) => !selectedErrorRows.has(index));
      setErrorModalContent(remainingErrors);
      setSelectedErrorRows(new Set()); // Limpiar selección
      setSnackbar({ open: true, message: `${selectedErrorRows.size} error${selectedErrorRows.size > 1 ? 'es' : ''} eliminado${selectedErrorRows.size > 1 ? 's' : ''} de la lista.`, severity: 'info' });
      if (remainingErrors.length === 0) {
          setOpenErrorModal(false); // Cerrar modal si ya no hay errores
      }
    };


    const handleSelectErrorRow = (index) => {
      setSelectedErrorRows(prevSelected => {
          const newSelected = new Set(prevSelected);
          if (newSelected.has(index)) {
            newSelected.delete(index);
          } else {
            newSelected.add(index);
          }
          return newSelected;
      });
    };


    const handleSelectAllErrorRows = (event) => {
      if (event.target.checked) {
        const allIndices = errorModalContent.map((_, index) => index);
        setSelectedErrorRows(new Set(allIndices));
      } else {
        setSelectedErrorRows(new Set());
      }
    };

    // --- Renderizado Condicional (Loader/Contenido) ---

    if (isLoading.pruebas) { // Mostrar loader principal solo mientras cargan las pruebas iniciales
      return (
        <SigertreLoader
          loaderText="SIGERTRE"
          message="Cargando pruebas..."
          fullScreen
        />
      );
    }

    if (!competencia) {
      return (
          <Box sx={{p: 3, textAlign: 'center'}}>
              <Typography color="text.secondary">Seleccione una competencia para ver sus pruebas.</Typography>
          </Box>
      );
    }

    // --- Renderizado Principal ---
    return (
      // Mantener sólo posición relativa para overlay; sin padding extra para igualar ancho con Categorías
  <Box sx={{ position: 'relative', width: '100%' }}>

          {/* Overlay de Carga para Acciones */}
          {isLoading.acciones && (
              <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  display: 'flex',
                  flexDirection: 'column', // Para alinear texto debajo
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1200, // Encima de otros elementos pero debajo de modales/dialogs
                  borderRadius: 1, // Opcional: redondear esquinas
                }}
              >
                <CircularProgress />
                <Typography sx={{ mt: 2 }} color="text.secondary">Procesando...</Typography>
              </Box>
          )}


  <Box mb={4}>
    <Typography variant="h6" gutterBottom>
      Gestión de Pruebas
    </Typography>

        <Grid container spacing={2} disableEqualOverflow justifyContent="flex-start" alignItems="stretch">
  <Grid item xs={12} sm={6} md={3}>
      {/* Nueva Personalizada (soft primary outline) */}
      <Button
        fullWidth
        onClick={() => setOpenCustomPruebaModal(true)}
        startIcon={<AddIcon />}
        disabled={isLoading.acciones}
  sx={(t) => ({
    borderRadius: 999,
    px: { xs: 1.25, sm: 1.75 },
    py: 1.1,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: { xs: '0.85rem', sm: '0.9rem' },
    bgcolor: 'transparent',
    color: t.palette.primary.main,
    border: `1px solid ${alpha(t.palette.primary.main, 0.35)}`,
    '&:hover': { bgcolor: alpha(t.palette.primary.main, 0.06) },
    '&.Mui-disabled': {
      color: alpha(t.palette.text.primary, 0.35),
      borderColor: alpha(t.palette.text.primary, 0.12),
    },
  })}
      >
        {isLoading.acciones ? <CircularProgress size={13} sx={{ mr: 1 }} /> : null}
        Personalizada
      </Button>
      </Grid>
  <Grid item xs={12} sm={6} md={3}>
      {/* Predeterminadas (soft secondary) */}
      <Button
      fullWidth
        onClick={() => setOpenPredefinedModal(true)}
        startIcon={<AddIcon />}
        disabled={isLoading.acciones}
        sx={(t) => ({
    borderRadius: 999,
    px: { xs: 1.25, sm: 1.75 },
    py: 1.1,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: { xs: '0.85rem', sm: '0.9rem' },
    bgcolor: alpha(t.palette.secondary.main, 0.08),
    color: t.palette.secondary.main,
    border: `1px solid ${alpha(t.palette.secondary.main, 0.25)}`,
    boxShadow: `0 4px 18px ${alpha(t.palette.secondary.main, 0.18)}`,
    transition: 'transform .04s ease, box-shadow .2s ease, background .2s',
    '&:hover': {
      bgcolor: alpha(t.palette.secondary.main, 0.12),
      boxShadow: `0 8px 28px ${alpha(t.palette.secondary.main, 0.25)}`,
    },
    '&:active': { transform: 'translateY(1px)' },
    '&.Mui-disabled': {
      bgcolor: alpha(t.palette.text.primary, 0.06),
      color: alpha(t.palette.text.primary, 0.35),
      borderColor: alpha(t.palette.text.primary, 0.12),
      boxShadow: 'none',
    },
        })}
      >
        {isLoading.acciones ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
        Predeterminadas
      </Button>
    </Grid>

      <Grid item xs={12} sm={6} md={3}>
      {/* Importar CSV */}
      <Button
      fullWidth
        onClick={handleSelectFile}
        startIcon={<UploadFileIcon />}
        disabled={isLoading.acciones}
        sx={(t) => ({
          borderRadius: 999,
          px: { xs: 1.25, sm: 1.75 },
          py: 1.1,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: { xs: '0.85rem', sm: '0.9rem' },
          bgcolor: 'transparent',
          color: t.palette.text.primary,
          border: `1px solid ${alpha(t.palette.text.primary, 0.25)}`,
          '&:hover': { bgcolor: alpha(t.palette.text.primary, 0.06) },
        })}
      >
        Importar CSV
      </Button>
      <input type="file" accept=".csv, text/csv" hidden ref={fileInputRef} onChange={handleImportCSV} />
      </Grid>
  <Grid item xs={12} sm={6} md={3}>

      {/* Descargar CSV */}
      <Button
      fullWidth
        onClick={handleDownloadCSV}
        startIcon={<DownloadIcon />}
        disabled={isLoading.acciones}
        sx={(t) => ({
          borderRadius: 999,
          px: { xs: 1.25, sm: 1.75 },
          py: 1.1,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: { xs: '0.85rem', sm: '0.9rem' },
          bgcolor: 'transparent',
          color: t.palette.text.primary,
          border: `1px solid ${alpha(t.palette.text.primary, 0.25)}`,
          '&:hover': { bgcolor: alpha(t.palette.text.primary, 0.06) },
        })}
      >
        Descargar CSV
      </Button>
      </Grid>
      </Grid>



  </Box>


        {/* Modal para Prueba Personalizada */}
        <CustomPruebaModal
          isOpen={openCustomPruebaModal}
          onClose={() => setOpenCustomPruebaModal(false)}
          onSave={handleAddPruebaCustom} // Usa el handler actualizado
          pruebaData={newPruebaCustom}
          onDataChange={setNewPruebaCustom}
          categorias={categorias}
        />
        {/* Tabla de Pruebas */}
  {/* Lista de pruebas: solo uno según breakpoint */}
  <Box sx={{ mb: 3 }}>
    {isMobile ? (
      <MobilePruebasView
        pruebas={pruebas}
        categorias={categorias}
        search={search}
        setSearch={setSearch}
        onUpdatePrueba={handleUpdatePrueba}
        onDeletePrueba={handleDeletePrueba}
        onAddPruebaCustom={handleAddPruebaCustom}
        onAddSelectedPruebas={handleAddSelectedPruebas}
        setSnackbar={setSnackbar}
        onRequestDeleteMultiple={(ids) => {
          setSelectedPruebaIds(ids);        // guardas la selección
          setOpenDeleteDialog(true);        // abres el modal de confirmación
        }}
      />
    ) : (
      <PruebasTable
        pruebas={pruebas}
        categorias={categorias}
        onUpdatePrueba={handleUpdatePrueba}
        onDeletePrueba={handleDeletePrueba}
        setSnackbar={setSnackbar}
      />
    )}
  </Box>


        {/* Modal de Pruebas Predeterminadas */}
        <PredefinedPruebasModal
          open={openPredefinedModal}
          handleClose={() => setOpenPredefinedModal(false)}
          handleAddSelectedPruebas={handleAddSelectedPruebas} // Usa handler actualizado
          categorias={categorias} // Pasar categorías cargadas
        />

        {/* Modal de Errores de Importación */}
        <Dialog
          open={openErrorModal}
          onClose={() => setOpenErrorModal(false)}
          aria-labelledby="error-modal-title"
          fullWidth
          maxWidth="lg" // Suficientemente ancho para la tabla de errores
        >
          <DialogTitle id="error-modal-title">Errores al Importar Pruebas desde CSV</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Las siguientes filas del archivo CSV contienen errores y no pudieron ser importadas.
              Puedes corregir el archivo CSV o eliminar las filas con errores de esta vista previa.
            </DialogContentText>

            {/* Toolbar para acciones en filas seleccionadas */}
            {errorModalContent.length > 0 && selectedErrorRows.size > 0 && ( // Mostrar solo si hay selección
              <Toolbar sx={{ justifyContent: 'space-between', bgcolor: 'action.selected', borderRadius: 1, mb: 1 }}>
                <Typography variant="subtitle1" component="div">
                  {selectedErrorRows.size} fila{selectedErrorRows.size > 1 ? 's' : ''} seleccionada{selectedErrorRows.size > 1 ? 's' : ''}
                </Typography>
                <Tooltip title="Eliminar filas seleccionadas de esta vista">
                  <IconButton onClick={handleDeleteSelectedErrors} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Toolbar>
            )}

            <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}> {/* Limitar altura y añadir scroll */}
              <TableContainer component={Paper} variant="outlined"> {/* Usar outlined */}
                <Table stickyHeader aria-label="tabla de errores de importación">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          indeterminate={selectedErrorRows.size > 0 && selectedErrorRows.size < errorModalContent.length}
                          checked={errorModalContent.length > 0 && selectedErrorRows.size === errorModalContent.length}
                          onChange={handleSelectAllErrorRows}
                          inputProps={{ 'aria-label': 'seleccionar todas las filas con errores' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Fila CSV</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Nombre Prueba (Input)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>eventCode (Input)</TableCell>
                      {/* <TableCell sx={{ fontWeight: 'bold' }}>#CatFemenil (Input)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>#CatVaronil (Input)</TableCell> */}
                      <TableCell sx={{ fontWeight: 'bold', minWidth: '300px' }}>Errores Detectados</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {errorModalContent.map((error, index) => (
                      <TableRow
                          key={index}
                          hover
                          onClick={() => handleSelectErrorRow(index)} // Seleccionar al hacer clic en la fila
                          role="checkbox"
                          aria-checked={selectedErrorRows.has(index)}
                          tabIndex={-1}
                          selected={selectedErrorRows.has(index)}
                          sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={selectedErrorRows.has(index)}
                            inputProps={{ 'aria-labelledby': `error-row-${index}` }}
                          />
                        </TableCell>
                        <TableCell>{error.fila ?? 'N/A'}</TableCell> {/* Mostrar Fila */}
                        <TableCell>{error.nombre ?? ''}</TableCell> {/* Mostrar el nombre que se intentó importar */}
                        <TableCell
                          sx={{ bgcolor: error.razones?.some(r => r.toLowerCase().includes('eventcode')) ? 'error.lighter' : 'inherit' }}
                        >
                          {error.eventCode ?? ''}
                        </TableCell>
                        {/* <TableCell
                          sx={{ bgcolor: error.razones?.some(r => r.toLowerCase().includes('femenil')) ? 'error.lighter' : 'inherit' }}
                        >
                          {error['#CatFemenil'] ?? ''}
                        </TableCell>
                        <TableCell
                          sx={{ bgcolor: error.razones?.some(r => r.toLowerCase().includes('varonil')) ? 'error.lighter' : 'inherit' }}
                        >
                          {error['#CatVaronil'] ?? ''}
                        </TableCell> */}
                        <TableCell>
                          {Array.isArray(error.razones) && error.razones.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                                {error.razones.map((razon, idx) => (
                                  <li key={idx}><Typography variant="body2" color="error">{razon}</Typography></li>
                                ))}
                            </ul>
                          ) : (
                              <Typography variant="body2" color="text.secondary">Error desconocido</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}> {/* Mejorar espaciado */}
            <Button
              onClick={handleDeleteSelectedErrors}
              color="error"
              variant="outlined" // Cambiar a outlined para menos énfasis que Aceptar
              disabled={selectedErrorRows.size === 0}
              startIcon={<DeleteIcon />}
            >
              Eliminar Seleccionados ({selectedErrorRows.size})
            </Button>
            <Button onClick={() => setOpenErrorModal(false)} color="primary" variant="contained">
              Cerrar Ventana
            </Button>
          </DialogActions>
        </Dialog>

  {/* Accordion para Opciones Avanzadas */}
  <Box sx={{ mt: 3 }}> {/* Añadido margen superior */}
          <CustomAccordion title="Opciones Avanzadas de Registro">
            <AccordionDetails>
              <Grid container spacing={3} disableEqualOverflow alignItems="flex-start">
                {/* Máximo de Pruebas Individuales */}
                <Grid item xs={12} sm={6} lg={3}>
                  <TextField
                    label="Máx. Pruebas Indiv. por Atleta"
                    type="number"
                    // *** LEER DESDE EL ESTADO LOCAL DEL FORMULARIO ***
                    value={advancedRulesForm.maxPruebasIndPorRegistro}
                    onChange={(e) => {
                      const value = Math.max(0, Number(e.target.value) || 0);
                      if (value <= 15) {
                        // *** ACTUALIZAR ESTADO LOCAL ***
                        handleAdvancedRulesFormChange('maxPruebasIndPorRegistro', value);
                      } else {
                        setSnackbar({ open: true, message: 'Máximo permitido es 15.', severity: 'warning' });
                        // Opcional: resetear al valor anterior o al máximo
                        handleAdvancedRulesFormChange('maxPruebasIndPorRegistro', 15);
                      }
                    }}
                    variant="outlined" fullWidth size="small" InputLabelProps={{ shrink: true }} inputProps={{ min: 0, max: 15 }}
                  />
                </Grid>

                {/* Máximo de Relevos */}
                <Grid item xs={12} sm={6} lg={3}>
                  <TextField
                    label="Máx. Relevos por Equipo/Registro"
                    type="number"
                    // *** LEER DESDE EL ESTADO LOCAL DEL FORMULARIO ***
                    value={advancedRulesForm.maxRelevosPorRegistro}
                    onChange={(e) => {
                      const value = Math.max(1, Number(e.target.value) || 1);
                      if (value <= 15) {
                        // *** ACTUALIZAR ESTADO LOCAL ***
                        handleAdvancedRulesFormChange('maxRelevosPorRegistro', value);
                      } else {
                        setSnackbar({ open: true, message: 'Máximo permitido es 15.', severity: 'warning' });
                        handleAdvancedRulesFormChange('maxRelevosPorRegistro', 15);
                      }
                    }}
                    variant="outlined" fullWidth size="small" InputLabelProps={{ shrink: true }} inputProps={{ min: 1, max: 15 }}
                  />
                </Grid>

                {/* Habilitar Marcas de Siembra */}
                <Grid item xs={12} sm={6} lg={3}>
                  <Typography variant="body2" gutterBottom sx={{fontWeight: 'medium'}}>Marcas de Siembra</Typography>
                  <FormControlLabel
                    control={ <Checkbox size="small"
                        // *** LEER DESDE EL ESTADO LOCAL DEL FORMULARIO ***
                        checked={advancedRulesForm.habilitarMarcasSiembra}
                        // *** ACTUALIZAR ESTADO LOCAL ***
                        onChange={(e) => handleAdvancedRulesFormChange('habilitarMarcasSiembra', e.target.checked)}
                        color="primary" /> }
                    label="Habilitar registro"
                  />
                </Grid>

                {/* Requerir Miembros de Relevo */}
                <Grid item xs={12} sm={6} lg={3}>
                  <Typography variant="body2" gutterBottom sx={{fontWeight: 'medium'}}>Miembros del Relevo</Typography>
                  <Box>
                      <FormControlLabel
                        control={ <Checkbox size="small"
                          // *** LEER DESDE EL ESTADO LOCAL DEL FORMULARIO ***
                          checked={advancedRulesForm.requerirMiembrosRelevo}
                          // *** ACTUALIZAR ESTADO LOCAL ***
                          onChange={(e) => handleAdvancedRulesFormChange('requerirMiembrosRelevo', e.target.checked)}
                          color="primary" /> }
                        label="Requerir al registrar"
                      />
                      {/* El botón para abrir el modal de relevos depende del estado local ahora */}
                      {advancedRulesForm.requerirMiembrosRelevo && (
                        <Button size="small" variant="text" color="primary" onClick={() => setOpenRelevosModal(true)} sx={{ ml: 1, textTransform: 'none' }}> Configurar Relevos... </Button>
                      )}
                  </Box>
                </Grid>
              </Grid>

              {/* *** NUEVO: Botón Guardar Cambios *** */}
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveAdvancedRules}
                  disabled={!hasAdvancedChanges || isLoading.acciones} // Deshabilitado si no hay cambios o está cargando
                >
                  Guardar Cambios en Opciones Avanzadas
                </Button>
              </Stack>

            </AccordionDetails>
          </CustomAccordion>
        </Box>

        {/* Modal de Opciones de Relevos */}
        <Dialog
          open={openRelevosModal}
          onClose={() => setOpenRelevosModal(false)}
          aria-labelledby="opciones-relevos-dialog-title"
          fullWidth
          maxWidth="xs" // Hacerlo más pequeño
        >
          <DialogTitle id="opciones-relevos-dialog-title">Configuración de Relevos</DialogTitle>
          <DialogContent dividers> {/* Añadir divisores */}
            {/* <DialogContentText sx={{mb: 2}}>
              Define los límites y opciones para los equipos de relevo.
            </DialogContentText> */}
            <Grid container spacing={2} disableEqualOverflow direction="column">
              {/* Número Mínimo de Miembros */}
              <Grid item>
                <TextField
                  label="Mínimo Miembros"
                  type="number"
                  value={opcionesRelevosForm.minMiembros}
                  onChange={(e) => handleOpcionesRelevosChange('minMiembros', Math.max(0, Number(e.target.value) || 0))}
                  variant="outlined"
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, max: opcionesRelevosForm.maxMiembros }}
                />
              </Grid>
              {/* Número Máximo de Miembros */}
              <Grid item>
                <TextField
                  label="Máximo Miembros"
                  type="number"
                  value={opcionesRelevosForm.maxMiembros}
                  onChange={(e) => handleOpcionesRelevosChange('maxMiembros', Math.max(opcionesRelevosForm.minMiembros, Number(e.target.value) || 0))}
                  variant="outlined"
                  fullWidth
                  size="small"
                  inputProps={{ min: opcionesRelevosForm.minMiembros, max: 8 }} // Límite práctico
                />
              </Grid>
              {/* Permitir Equipos Mixtos */}
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={opcionesRelevosForm.permitirEquiposMixtos}
                      onChange={(e) => handleOpcionesRelevosChange('permitirEquiposMixtos', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Permitir equipos mixtos"
                />
              </Grid>
              {/* Permitir Suplentes en el Equipo */}
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={opcionesRelevosForm.permitirSuplentes}
                      onChange={(e) => handleOpcionesRelevosChange('permitirSuplentes', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Permitir suplentes"
                />
                {/* Input para número de suplentes si está activado */}
                {opcionesRelevosForm.permitirSuplentes && (
                  <TextField
                    label="Número de Suplentes"
                    type="number"
                    value={opcionesRelevosForm.numeroSuplentes}
                    // Asegurar que sea al menos 1 si se permiten suplentes
                    onChange={(e) => handleOpcionesRelevosChange('numeroSuplentes', Math.max(1, Number(e.target.value) || 1))}
                    variant="outlined"
                    fullWidth
                    size="small"
                    inputProps={{ min: 1, max: 4 }} // Límite práctico
                    sx={{ mt: 1 }} // Margen superior
                  />
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRelevosModal(false)} color="secondary">
              Cancelar
            </Button>
            <Button onClick={handleSaveOpcionesRelevos} color="primary" variant="contained">
              Guardar Opciones
            </Button>
          </DialogActions>
        </Dialog>
        {/* Modal de confirmación para eliminar pruebas seleccionadas (móvil y/o masivo) */}
  <Dialog
    open={openDeleteDialog}
    onClose={() => setOpenDeleteDialog(false)}
    aria-labelledby="confirm-delete-pruebas-title"
  >
    <DialogTitle id="confirm-delete-pruebas-title">
      Eliminar pruebas seleccionadas
    </DialogTitle>
    <DialogContent>
      <DialogContentText>
        ¿Seguro que deseas eliminar {selectedPruebaIds.length} prueba{selectedPruebaIds.length > 1 ? 's' : ''}? Esta acción no se puede deshacer.
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setOpenDeleteDialog(false)} color="secondary">
        Cancelar
      </Button>
      <Button
        color="error"
        variant="contained"
        disabled={isLoading.acciones || selectedPruebaIds.length === 0}
        onClick={async () => {
          try {
            setIsLoading(prev => ({ ...prev, acciones: true }));
            await Promise.all(selectedPruebaIds.map((id) => handleDeletePrueba(id)));
            setSnackbar({
              open: true,
              message: `${selectedPruebaIds.length} prueba${selectedPruebaIds.length > 1 ? 's' : ''} eliminada${selectedPruebaIds.length > 1 ? 's' : ''}.`,
              severity: 'success',
            });
          } catch (e) {
            setSnackbar({ open: true, message: e?.message || 'Error al eliminar.', severity: 'error' });
          } finally {
            setIsLoading(prev => ({ ...prev, acciones: false }));
            setOpenDeleteDialog(false);
            setSelectedPruebaIds([]);
          }
        }}
      >
        {isLoading.acciones ? <CircularProgress size={24} color="inherit" /> : 'Eliminar'}
      </Button>
    </DialogActions>
  </Dialog>


        {/* Snackbar para notificaciones generales */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {/* Asegurarse que Alert tenga onClose para poder cerrarlo manualmente */}
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  };

  export default PruebasManager;
